import pluralize from "pluralize";
import React from "react";
import { Card, Container } from "react-bootstrap";
import { useParams } from "react-router-dom";

import attachmentApi, { getDownloadPathWithToken } from "../../api/attachment";
import { useInvoiceWithToken } from "../../api/invoice";
import { usePaymentWithToken } from "../../api/payment";
import { useProjectWithToken } from "../../api/project";
import {
    FileRow,
    InvalidLinkPage,
    SinglePageContainer,
} from "../../common/components";
import { Loader } from "../../common/components/core";
import AttachmentContext from "../../common/contexts/AttachmentContext";
import { useAuth, useAuthTokenRehydrate } from "../../common/hooks";
import { InvoiceState } from "../../types/invoice";
import { PaymentStateWithDocuments } from "../../types/payment";
import { BesuProjectState } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";

const InvoiceDownloadView = (): JSX.Element => {
    document.title = PageTitle.InvoiceDownloadView;
    const { token } = useAuth();
    const { isHydrated, error: authError } = useAuthTokenRehydrate();
    const { projectId, invoiceId, paymentId } = useParams<{
        projectId: string;
        invoiceId: string;
        paymentId: string;
    }>();
    const {
        data: project,
        error: projectError,
        isValidating: isProjectLoading,
    } = useProjectWithToken(projectId);
    const {
        data: invoice,
        error: invoiceError,
        isValidating: isInvoiceLoading,
    } = useInvoiceWithToken(invoiceId);
    const {
        data: payment,
        error: paymentError,
        isValidating: isPaymentLoading,
    } = usePaymentWithToken(paymentId);

    return (
        <AttachmentContext.Provider
            value={{
                ...attachmentApi,
                getDownloadPath: getDownloadPathWithToken(token),
            }}
        >
            <Loader
                isLoading={!isHydrated}
                error={!!authError}
                errorFallback={<InvalidLinkPage />}
            >
                <Loader
                    isLoading={
                        isPaymentLoading || isInvoiceLoading || isProjectLoading
                    }
                    error={!!(paymentError || invoiceError || projectError)}
                    errorFallback={<InvalidLinkPage />}
                >
                    {project && invoice && payment && (
                        <PureInvoiceDownloadView
                            project={project}
                            invoice={invoice.state.data}
                            payment={payment}
                        />
                    )}
                </Loader>
            </Loader>
        </AttachmentContext.Provider>
    );
};

type PureInvoiceDownloadViewProps = {
    payment: PaymentStateWithDocuments;
    invoice: InvoiceState;
    project: BesuProjectState;
};

export const PureInvoiceDownloadView = ({
    project,
    invoice,
    payment,
}: PureInvoiceDownloadViewProps): JSX.Element => {
    const { approver, paymentState, sharedDocument } = payment;
    const developerAccountName = paymentState.payer.externalRef;
    const contractorAccountName = paymentState.payee.externalRef;
    return (
        <SinglePageContainer>
            <Container>
                <h2 className="mt-6 mb-2">
                    {pluralize("Invoices", sharedDocument.length)} shared by{" "}
                    {project.developer?.displayName ?? project.developer?.accountName ?? project.name}
                </h2>
                <p className="mb-5">
                    Please note that the following invoices has been shared to
                    HSBC.
                    <br />
                    <strong>Approved by: {approver}</strong>
                    <br />
                    <strong>
                        Claim #{shortenUUID(invoice.props.claimLinearId.id)}
                    </strong>
                    <br />
                    <strong>Invoice #{shortenUUID(invoice.linearId.id)}</strong>
                    <br />
                    <strong>
                        Payment Order #{paymentState.customerRefNumber}
                    </strong>
                </p>
                <Card body className="mb-5 border-0">
                    <h3 className="mb-3">
                        {pluralize("Invoice", sharedDocument.length)}
                    </h3>
                    {sharedDocument.map((file, index) => (
                        <FileRow
                            key={index}
                            file={{
                                id: file.documentHash,
                                name: file.documentName,
                            }}
                            canDownload
                            downloadMeta={{
                                developerAccountName,
                                contractorAccountName,
                            }}
                        />
                    ))}
                </Card>
            </Container>
        </SinglePageContainer>
    );
};

export default InvoiceDownloadView;
